<template>
  <div class="auth-main-wrapper col-lg-5 col-md-6 col-sm-12 mt-5">
    <div class="auth-inner-div w-100 text-center">
      <h3 class="auth-header">{{ $t('customer_self_service') }}</h3>
    </div>
    <div class="auth-inner-form-div">
      <div class="auth-input-wrapper w-100">
        <span v-if="isLogin">
          <span class="error-color font-small" v-if="responseStatus === 401">{{ $t('invalid_user') }}</span>
          <span class="error-color font-small" v-if="responseStatus === 403">{{ $t('account_not_verified') }}</span>
          <span class="error-color font-small" v-if="responseStatus === 422">{{ $t('invalid_inputs') }}</span>
        </span>
        <InputComponent v-for="input in form.inputs"
                        class="w-100 auth-input mx-auto"
                        :name-input="input.name"
                        :data-type="input.dataType"
                        :placeholder="$t(input.placeholder)"
                        v-model:data="input.value"
                        :msg="input.validation"></InputComponent>
      </div>
    </div>
    <div class="auth-inner-div w-100">
      <router-link class="blue" to="/forgot-password">{{ $t('forgot_password') }}</router-link>
    </div>
    <div class="auth-inner-div w-100">
      <button class="auth-customer-login-button w-100"
              @click.prevent="login"
              @keydown.enter="login"
              :disabled="loading">
        {{ $t('login') }}
      </button>
    </div>
    <div class=" w-100 ">
      <span class="error-color font-small" v-if="isSocialLogin && responseStatus">
        {{ responseStatus === 401 ? $t('social_login_error') : '' }}
        {{ responseStatus === 403 ? $t('account_not_verified') : '' }}
        {{ responseStatus === 422 ? $t('declined_login_method') : '' }}
      </span>
      <div class="auth-inner-div w-100" v-if="store.state.socialAuth.google_login === '1'">
        <button class="auth-soc-buttons w-100" :disabled="loading" @click="socialLogin(SocialAuthProviders.GOOGLE)">
          <span class="auth-button-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M19.7875 10.2271C19.7875 9.51801 19.7239 8.83619 19.6057 8.18164H10.1875V12.0498H15.5693C15.3375 13.2998 14.633 14.3589 13.5739 15.068V17.5771H16.8057C18.6966 15.8362 19.7875 13.2726 19.7875 10.2271Z" fill="#4285F4"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.1871 20.0004C12.8871 20.0004 15.1508 19.105 16.8053 17.5777L13.5735 15.0686C12.678 15.6686 11.5326 16.0231 10.1871 16.0231C7.58256 16.0231 5.37801 14.264 4.59165 11.9004H1.25073V14.4913C2.89619 17.7595 6.27802 20.0004 10.1871 20.0004Z" fill="#34A853"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M4.59205 11.8997C4.39205 11.2997 4.27842 10.6588 4.27842 9.99971C4.27842 9.34062 4.39205 8.6997 4.59205 8.0997V5.50879H1.25114C0.573864 6.85879 0.1875 8.38607 0.1875 9.99971C0.1875 11.6133 0.573864 13.1406 1.25114 14.4906L4.59205 11.8997Z" fill="#FBBC05"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.1871 3.97728C11.6553 3.97728 12.9735 4.48183 14.0099 5.47274L16.878 2.60455C15.1462 0.990911 12.8826 0 10.1871 0C6.27802 0 2.89619 2.24091 1.25073 5.5091L4.59165 8.10002C5.37801 5.73638 7.58256 3.97728 10.1871 3.97728Z" fill="#EA4335"/>
            </svg>
          </span>
          {{ $t('google_login') }}
        </button>
      </div>

      <div class="auth-inner-div w-100" v-if="store.state.socialAuth.facebook_login === '1'">
        <button class="auth-soc-buttons w-100" :disabled="loading" @click="socialLogin(SocialAuthProviders.FACEBOOK)">
            <span class="auth-button-icon">
              <svg width="12" height="23" viewBox="0 0 12 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.65708 12.3648H11.3793L11.9637 8.56055H7.65632V6.48134C7.65632 4.90099 8.16957 3.49962 9.63891 3.49962H12V0.179741C11.5852 0.123382 10.7078 0 9.04996 0C5.58819 0 3.55867 1.8393 3.55867 6.0297V8.56055H0V12.3648H3.55867V22.821C4.26344 22.9276 4.97729 23 5.71007 23C6.37245 23 7.01893 22.9391 7.65708 22.8522V12.3648Z" fill="#0029FF"/>
              </svg>
            </span>
          {{ $t('facebook_login') }}
        </button>
      </div>
    </div>
    <div class="auth-inner-div auth-bottom-sentence w-100 text-center">
      {{ $t('dont_have_account') + ' ' }}
      <router-link class="blue" to="/register">{{ $t('register') }}</router-link>
    </div>
  </div>
</template>

<script lang="ts">
import "./customerLogin.scss"

// @ts-ignore
import InputComponent from "../input/InputComponent.vue";
import type {IForm} from "@/interfaces/IForm.interface";
import type {IInput} from "@/interfaces/IInput.interface";
import axios from "axios";
import FormValidationService from "../../services/formValidationService";
import {useStore} from "vuex";
import {defineComponent} from "vue";
import apiService from "../../services/apiService";
import NameOfHelper from "../../helpers/nameOfHelper";
import {SocialAuthProviders} from "../../interfaces/enums/SocialAuthProviders.enum";
import type {ILoginResponse} from "../../interfaces/ILoginResponse.interface";

// vue-social-auth v1.4.3
// NOTE Do not remove!
import oAuth from "../../plugins/oAuth.js";

import type {ICustomerDataResponse} from "@/interfaces/ICustomerDataResponse.interface";
import authService from "../../services/authService"; // Responsible for this.$auth to work
import {routesConst} from "../../routes.js";

export default defineComponent({
  name: "CustomerLoginComponent",
  components: {
    InputComponent,
  },
  setup() {
    const store = useStore();

    return {
      store
    }
  },
  data() {
    return {
      form: <IForm>{
        isValid: false,
        inputs: <IInput[]>[
          <IInput>{
            key: 'email', // NOTE prop binded with store
            name: 'email',
            dataType: 'text',
            placeholder: 'e_mail',
            value: null,
            validation: {
              state: true,
              message: null,
              rules: {
                required: {message: 'Please_Enter_your_email'},
                invalid_user: {message:''},
                not_verified: {message: ''},
              }
            }
          },
          <IInput>{
            key: 'password', // NOTE prop binded with store
            name: 'password',
            dataType: 'password',
            placeholder: 'password',
            value: null,
            validation: {
              state: true,
              message: null,
              rules: {
                required: {},
                invalid_user: {},
                not_verified: {},
              }
            }
          },
        ],
      },
      SocialAuthProviders: SocialAuthProviders,
      isLogin: false,
      isSocialLogin: false,
      loading: false,
      responseStatus: 0,
    }
  },
  mounted() {
    const hash = this.$route.params.hash

    if(hash) {
      axios.post(apiService.customerActivate(), {'hash': hash})
          .then(response => {
          })
          .catch(err => {
            console.error(err);
            this.$router.push(routesConst.HOME);
          })
    }
  },
  methods: {
    login(): void {
      this.loading = true;

      this.isLogin = true;
      this.isSocialLogin = false;

      if (! FormValidationService.validate(this.form.inputs)) {
        this.loading = false;
        return;
      }

      const data = {'timezone': Intl.DateTimeFormat().resolvedOptions().timeZone};
      for(const prop in this.form.inputs) {
        if(! this.form.inputs.hasOwnProperty(prop)) {
          continue;
        }
        const item = this.form.inputs[prop];
        data[item.key] = item.value;

      }

      axios.post(apiService.authLogin(), data)
          .then(response => {
            this.handleResponse(response);
          })
          .catch(error => {
            this.handleError(error);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    socialLogin(provider: string): void {
      const _this = this;

      this.loading = true;

      this.isLogin = true;
      this.isSocialLogin = false;

      // @ts-ignore
      this.$auth().authenticate(provider)
          .then(response =>{
            socialLoginPartTwo(response);
            this.loading = false;
          })
          .catch(error => {
            console.error(error);
          })
          // .finally(() => {
            //   this.loading = false;
            // });
            
          function socialLoginPartTwo( authResponse: any): void {
            axios.post(apiService.authSocialLogin() + provider, authResponse)
            .then(response => {
              _this.handleResponse(response);
            })
            .catch(error => {
              _this.handleError(error);
            })
            .finally(() => {
              _this.loading = false;
          });
      }
    },
    handleResponse(response): void {
      const data = <ILoginResponse> response.data;
      const user = <ICustomerDataResponse> data.user;

      const state = this.store.state.customer;
      const nameOf = (c) => NameOfHelper.obj(c);

      this.store.commit( 'customer/SET_STATE_PROP_VALUE', [
        {prop: nameOf(() => state.token), value: data.token},
        {prop: nameOf(() => state.expire_time), value: data.expires_in},
        {prop: nameOf(() => state.first_name), value: user.first_name},
        {prop: nameOf(() => state.last_name), value: user.last_name},
        {prop: nameOf(() => state.email), value: user.email},
        {prop: nameOf(() => state.mobno), value: user.mobno},
        {prop: nameOf(() => state.company_name), value: user.company_name},
        {prop: nameOf(() => state.company_number), value: user.company_number },
        {prop: nameOf(() => state.company_vat), value: user.company_vat},
        {prop: nameOf(() => state.address), value: user.address},
      ]);

      new authService(this.store, this.$router).init();

      this.$router.push(routesConst.HOME);
    },
    handleError(error): void {
      if (! error.response) {
        console.error(error);
        return;
      }
      this.responseStatus = error.response.status;

      if (! [
        401,
        403, // account_not_verified
        422 // declined_login_method
      ].includes(this.responseStatus)
      ) {
        console.error(error);
      }
    }
  }
});
</script>

<style scoped>

</style>